import { createDocEntryStore } from './docStore';
import { readState } from './docState';
import { standardRgb, standardSurface, standardTexture, type MaterialTexture } from './standardMaterials';
import { materialsFor } from './materialsRun';
import { getSubmodelFilter } from './submodelFilterStore';
import { materialText, type MaterialPair } from './variantLabel';
import type { Surface } from './threeDApi';
import type { Rgb } from './sampleColour';

// WHAT ONE OF THIS NOTICE'S MATERIALS LOOKS LIKE — the three numbers glTF stores.
//
// Phase 1 reads what the product is made of, in the retailer's words: "Bois d'hévéa massif,
// foncé". Phase 2 says which part is made of which. Neither says what that material LOOKS
// like, and a mesh cannot be painted with a phrase.
//
// THE COLOUR IS SAMPLED, NEVER NAMED. There was a table in here mapping finish words to
// colours — "foncé" to [82, 54, 38], "effet blanchi" to [214, 200, 183] — and it was
// invention with a straight face: nobody measured those, they were my guess at what a stain
// looks like, and they would have been believed all the way to a painted model. The listing
// photographs of the assembled product ARE this material, in the finish it is sold in, and the
// listing-crops row has already found the parts in them. So a colour comes from a pixel, and
// it comes with the picture it was read off.
//
// ROUGHNESS AND METALLIC ARE JUDGED, never looked up. A listing material's come from the
// materials reading that is applied; a default material's from its own call (Read their
// textures). There was a table here mapping words to them — "steel" 0.35 and metal, "felt" 1 —
// and renaming a material silently changed how it took the light. Absent until one of those two
// has answered, and a material without them is not painted (see glb.painted).
export type MaterialSurface = {
  // sRGB 0-255, sampled off a picture. Absent until somebody picks one: unpainted is a state,
  // and a colour nobody read is worse than none.
  rgb?: Rgb;
  // Which picture said so. Provenance, exactly as a mesh's colour edit records it: three
  // digits with no story are three digits nobody can argue with.
  fromUrl?: string;
  // 0..1, judged — see above. Absent until judged.
  roughness?: number;
  metallic?: number;
  // WHETHER THE SURFACE IS FIBROUS, which is the one thing about a material the two numbers
  // above cannot say. Roughness is a single value for the whole surface: it can say "no
  // highlight", and it cannot say "and it is different at every square millimetre". Felt is
  // both, and the second is what makes a felt pad look like felt rather than like a beige
  // plastic disc — so it is asked for separately and answered with a picture. See fibre.py.
  // Judged with the other two.
  fibre?: boolean;
  // WHETHER THE COLOUR GOES ON FLAT OR THROUGH THE MESH'S OWN GRAIN. Chosen when the colour
  // is picked, and filed here with it so that every other use of this material makes the same
  // choice: the parts this colour spreads to, and the part somebody assigns to this material
  // tomorrow. Two legs of one chair, one grained and one flat because they were painted on
  // different days, is not a finish anybody chose.
  //
  // Absent means flat, which is what every colour picked before the choice existed was.
  grain?: boolean;
  // THE SURFACE DRAWN OVER A FLAT COLOUR — a default material's four numbers (see
  // standardMaterials), or a photographed material's as the applied reading measured it.
  texture?: MaterialTexture;
  // READINGS STRUCK OUT BY HAND, by crop key — the ones somebody looked at and said were not
  // this material: a mask that caught the floor, a photograph shot under a different lamp.
  //
  // FILED UNDER THE MATERIAL like the colour is, and for the same reason. The colour map pools
  // every reading of this wood across every part made of it, so "that one is wrong" is a
  // judgement about the wood and not about the part it happened to be opened from. Held as
  // component state at first, which meant it died with the modal and never reached the part
  // next door — the same map, the same readings, none of the exclusions.
  dropped?: string[];
};

// A material's identity, across the whole notice: its words, folded. Not its position in any
// list — the listings are re-read and the lists are rebuilt, and a surface filed under an
// index would come to describe a different material without anything changing here.
export const surfaceKey = (pair: MaterialPair) =>
  `${pair.material.trim().toLowerCase()}|${pair.color.trim().toLowerCase()}`;

type Stored = Record<string, MaterialSurface>;

const stateKey = (docId: string) => `notice.materialSurface.${docId}`;

const store = createDocEntryStore<{ byKey: Stored }>((docId) => ({
  snapshot: { byKey: readState<Stored>(docId, stateKey(docId), {}) },
}));

export const useMaterialSurfaces = store.useSnapshot;

// One material's surface: what has been recorded, over what was judged — the applied reading's
// numbers for a listing material, a default material's own for one of those. Roughness and
// metallic are absent where neither has answered, and the paint route then refuses.
//
// THREE MATERIALS ARRIVE WITH A COLOUR ALREADY. Not a reading, and not a violation of the
// rule above either: they are the ones no photograph will ever show, because the part is
// inside the joint. See standardMaterials, which argues it. They sit between the defaults and
// the stored surface, so a notice that DOES have a picture of its own hardware still wins.
export function surfaceOf(docId: string, pair: MaterialPair): MaterialSurface {
  const kept = store.get(docId).byKey[surfaceKey(pair)];
  const stated = standardRgb(docId, pair);
  const texture = standardTexture(docId, pair);
  // THE READING'S, for a material the listing names: the applied reading of the colourway on
  // screen, matched on the words the reading was asked to use.
  const name = materialText(pair).toLowerCase();
  const read = materialsFor(docId, getSubmodelFilter(docId)).materials
    .find((m) => m.name.trim().toLowerCase() === name);
  // ITS COLOUR AND TEXTURE TOO: a piece of hardware put on the chair's wood (a wood button) is
  // painted from this, and without the reading's colour it was not painted at all.
  const judged = read ? {
    rgb: read.rgb, roughness: read.roughness, metallic: read.metallic, fibre: read.fibre === true,
    ...(read.texture ? { texture: read.texture } : {}),
  } : {};
  return {
    ...judged, ...standardSurface(docId, pair),
    ...(stated ? { rgb: stated } : {}), ...(texture ? { texture } : {}), ...(kept || {}),
  };
}

// A MATERIAL AS THE PAINT ROUTE TAKES IT — its name and everything known about how its
// surface behaves, in one object.
//
// ONE PLACE THAT SAYS WHAT A MATERIAL IS. This was written out three times over — in the
// material menu, in the colour picker and in the paint a new mesh gets at birth — each
// listing the fields it happened to know about. They drifted the first time the list grew:
// `fibre` was added to two of the three, so picking felt from the menu went on painting
// plastic and the only sign was that the pad still looked like plastic. A material gaining
// a property must not be a thing to remember in three files.
//
// `grain` is the one argument, because it is the only part of this that is a decision about
// the MESH rather than a fact about the material: a reconstruction has a photograph of the
// real wood on it to keep, and a turned part has nothing. Defaulted to what the material
// records, which is what whoever picked the colour chose.
export function paintedAs(
  pair: MaterialPair, surface: MaterialSurface, grain = surface.grain === true,
): Surface {
  return {
    material: materialText(pair),
    roughness: surface.roughness,
    metallic: surface.metallic,
    fibre: surface.fibre === true,
    grain,
    ...(surface.texture ? { texture: surface.texture } : {}),
  };
}
